import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { Redirect, Route, RouteProps } from 'react-router-dom'

import { TokenInterface } from '@store/actionSlices/token'
import type { ProjectIdentity, RootStateTypeExtra, User } from '@store/types'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import { hasToken } from '@adUtilities/token-helper'

export type SessionActiveRoutes = {
  projectIdentity: ProjectIdentity
  session: SessionMap | undefined
  user: User
  token: TokenInterface
  returnPath: string
  authenticationPath: string
  fallbackPath?: string // For navigation menus
  isRouteEnabled?: boolean
} & RouteProps

function SessionActiveRoutes({
  session,
  user,
  projectIdentity,
  token,
  returnPath,
  authenticationPath,
  fallbackPath = '/vision',
  isRouteEnabled = true,
  ...routeProps
}: SessionActiveRoutes) {
  const [isAuthenticated, setIsAuthenticated] = React.useState(true)
  const [isSessionActive, setSessionActive] = React.useState(true)

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const setSessionOwner = async () => {
    const now = new Date()
    await firebaseControlQuery.updateSessionOwner([
      ...(session?.sessionOwners || []),
      {
        name: user.name,
        email: user.email,
        lastUpdated: now.toString(),
      },
    ])
  }

  useEffect(() => {
    if (!hasToken(token)) {
      setIsAuthenticated(false)
    }
  }, [token])

  useEffect(() => {
    const { sessionId, sessionList } = projectIdentity
    if (!sessionId && sessionList.length === 0 && !session?.connected) {
      setSessionActive(false)
    }
  }, [projectIdentity, session])

  useEffect(() => {
    if (session?.sessionOwners) {
      if (!session?.sessionOwners.find((res) => res.email === user.email)) {
        setSessionOwner()
      }
    }
  }, [session])

  if (!isAuthenticated) {
    return <Redirect to={{ pathname: authenticationPath }} />
  }

  if (!isSessionActive) {
    return <Redirect to={{ pathname: returnPath }} />
  }

  if (!isRouteEnabled) {
    return <Redirect to={{ pathname: fallbackPath }} />
  }

  return <Route {...routeProps} />
}

export default connect(
  ({
    projectIdentity,
    token,
    firestore: { session },
    user,
  }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
    token,
    user,
  })
)(SessionActiveRoutes)
